import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
} from "firebase/messaging";

const {
  VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID,
  VITE_FIREBASE_MEASUREMENT_ID,
  VITE_FIREBASE_FCM_VAPID_KEY,
} = import.meta.env;

// Firebase config (replace with actual values)
const firebaseConfig = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: VITE_FIREBASE_AUTH_DOMAIN,
  projectId: VITE_FIREBASE_PROJECT_ID,
  storageBucket: VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: VITE_FIREBASE_APP_ID,
  measurementId: VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

let swRegistration: ServiceWorkerRegistration | null = null;

const registerServiceWorker = async () => {
  if (swRegistration) return swRegistration;

  try {
    if (!("serviceWorker" in navigator)) {
      console.warn("Service worker not available in this browser/context");
      return null;
    }

    if (!window.isSecureContext) {
      console.warn(
        "Insecure context detected. Service workers require HTTPS or localhost. Skipping SW registration."
      );
      return null;
    }

    swRegistration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      {
        scope: "/", // Keep root scope
        updateViaCache: "none",
      }
    );
    console.log("SW registered with scope:", swRegistration.scope);
    return swRegistration;
  } catch (error) {
    console.error("SW registration failed:", error);
    return null;
  }
};

export const requestNotificationPermission =
  async (): Promise<NotificationPermission> => {
    console.log("Requesting notification permission...");
    if (!("Notification" in window)) {
      console.warn("Notifications API not supported in this environment");
      return "denied" as NotificationPermission;
    }

    if (Notification.permission === "granted") {
      return Notification.permission;
    }

    // Request permission and wait for the result
    const permission = await Notification.requestPermission();
    console.log("Notification permission status:", permission);
    return permission;
  };

const initializeMessaging = async (): Promise<Messaging> => {
  const supported = await isSupported();
  if (!supported) {
    throw new Error("Firebase messaging not supported");
  }
  return getMessaging(app);
};

export const fetchToken = async (retries = 3): Promise<string | null> => {
  let attempt = 0;
  while (attempt < retries) {
    try {
      attempt++;
      console.log(`Attempting to fetch FCM token (attempt ${attempt}/${retries})`);

      // Short-circuit in unsupported/insecure contexts
      const supported = await isSupported();
      if (!supported) {
        console.warn("Firebase messaging is not supported in this environment");
        return null;
      }

      if (!("serviceWorker" in navigator) || !window.isSecureContext) {
        console.warn(
          "Service worker not available or insecure context. Skipping FCM token fetch."
        );
        return null;
      }

      // 1. Register Service Worker first
      const swReg = await registerServiceWorker();

      // 2. Request notification permission
      const permission = await requestNotificationPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission not granted");
      }

      // 3. Initialize messaging
      const messagingInstance = await initializeMessaging();

      // 4. Get token
      console.log("Getting FCM token...");
      const token = await getToken(messagingInstance, {
        vapidKey: VITE_FIREBASE_FCM_VAPID_KEY,
        serviceWorkerRegistration: swReg ?? undefined,
      });

      if (!token) {
        throw new Error("Failed to get FCM token");
      }

      console.log("FCM token obtained successfully");
      return token;
    } catch (error) {
      console.error(`Error fetching FCM token (attempt ${attempt}):`, error);
      if (attempt === retries) {
        // Do not block app flow on FCM failure
        return null;
      }
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
  return null;
};

export { app, initializeMessaging };